import { z } from 'zod';

/**
 * The voice WebSocket (APP-104 §8): `GET /api/v1/feedback/voice`, same origin, with the session
 * cookie. Help chat's dictation uses it too (APP-124), with `scope: 'help'` in its `start`. The
 * browser sends `start` (within 10 s), then binary audio frames, then `stop`:
 *
 * - **Audio:** PCM, 16 kHz, 16-bit, mono, little-endian; about 100 ms (3,200 bytes) a frame, an
 *   even number of bytes and never more than 32,768. Send nothing before `started`.
 * - **The server's events** are JSON text messages. A session that ends in order sends `saved`;
 *   one that can't go on sends `error`. Either way `closed` follows, then the socket closes.
 *   A socket the browser drops gets nothing: what was heard is saved as interrupted.
 *
 * Only the fixed codes below ever reach the browser, never the speech service's own words.
 */

export const VOICE_PATH = '/api/v1/feedback/voice';

/** First, for feedback: the draft's ID and the ticket its open returned (one use, 2 minutes). */
export const FeedbackVoiceStart = z
  .object({ type: z.literal('start'), reportId: z.uuid(), ticket: z.string().min(1).max(128) })
  .strict();
/**
 * First, for a Help chat question (APP-124): the ticket `POST /v1/help/voice` returned (one use,
 * 2 minutes). The words go back to the question box only; nothing is kept but the minutes.
 */
export const HelpVoiceStart = z
  .object({
    type: z.literal('start'),
    scope: z.literal('help'),
    ticket: z.string().min(1).max(128),
  })
  .strict();
export const VoiceStart = z.union([FeedbackVoiceStart, HelpVoiceStart]);
export type VoiceStart = z.infer<typeof VoiceStart>;

/** Done: the server finishes the transcript, saves it, sends `saved`, and closes. */
export const VoiceStop = z.object({ type: z.literal('stop') }).strict();

export const VoiceClientMessage = z.union([FeedbackVoiceStart, HelpVoiceStart, VoiceStop]);
export type VoiceClientMessage = z.infer<typeof VoiceClientMessage>;

/**
 * Why a session couldn't start or go on.
 * - `no_start`: no `start` within 10 s of the upgrade.
 * - `bad_message`: anything that isn't this protocol (audio before `started`, a second `start`,
 *   an odd-length or empty frame, text that isn't one of the messages).
 * - `ticket_invalid`: no such ticket for this person, or a newer open replaced it, or the draft
 *   was discarded. `ticket_used`, `ticket_expired`: open the draft again for a new one.
 * - `report_sent`: the report has been sent; voice adds nothing to it now (feedback only).
 * - `unavailable`: voice isn't offered to this person now (a switch, speech not set up, the
 *   audience, feedback itself, or the session ended). Offer typing.
 * - `daily_limit`: no voice time left today (it resets at midnight, organization time).
 * - `too_many_sessions`: two of this person's sessions are already live.
 * - `busy`: this server is running all the sessions it takes; nothing was spent, try again.
 * - `frame_too_large`: a frame over 32,768 bytes.
 * - `backlog`: audio arrived faster than speech can take it (more than 5 s queued).
 * - `speech_*`: the speech service failed, by its fixed code.
 * - `server_closing`: the server is restarting; what was heard is saved.
 * - `internal`: something on our side failed.
 */
export const VOICE_ERROR_CODES = [
  'no_start',
  'bad_message',
  'ticket_invalid',
  'ticket_used',
  'ticket_expired',
  'report_sent',
  'unavailable',
  'daily_limit',
  'too_many_sessions',
  'busy',
  'frame_too_large',
  'backlog',
  'speech_unavailable',
  'speech_permission',
  'speech_quota',
  'speech_network',
  'speech_internal',
  'server_closing',
  'internal',
] as const;
export const VoiceErrorCode = z.enum(VOICE_ERROR_CODES);
export type VoiceErrorCode = z.infer<typeof VoiceErrorCode>;

/**
 * Why a session that ended in order ended: Done (`stopped`), the 5-minute cap
 * (`recording_cap`), the day's allowance running out mid-recording (`daily_limit`), 15 s of
 * audio below the silence line (`silence`; Help chat's dictation stops after 3 s once words are
 * heard, APP-174), or 15 s with no audio frames at all (`no_audio`).
 */
export const VOICE_END_REASONS = [
  'stopped',
  'recording_cap',
  'daily_limit',
  'silence',
  'no_audio',
] as const;
export const VoiceEndReason = z.enum(VOICE_END_REASONS);
export type VoiceEndReason = z.infer<typeof VoiceEndReason>;

/** A final phrase, and where it starts in the session's audio. */
export const VoicePhrase = z
  .object({ text: z.string(), offsetMs: z.number().int().nonnegative() })
  .strict();
export type VoicePhrase = z.infer<typeof VoicePhrase>;

export const VoiceServerEvent = z.discriminatedUnion('type', [
  /** Speech is listening: send the held audio now. `maxMs` is this session's audio cap. */
  z.object({ type: z.literal('started'), maxMs: z.number().int().positive() }).strict(),
  /** The words so far in the phrase being spoken (shown, never stored). */
  z.object({ type: z.literal('partial'), text: z.string() }).strict(),
  /** A final phrase. */
  VoicePhrase.extend({ type: z.literal('phrase') }).strict(),
  /** Every phrase as saved: the words to put in the review. */
  z
    .object({ type: z.literal('saved'), transcript: z.array(VoicePhrase), reason: VoiceEndReason })
    .strict(),
  z.object({ type: z.literal('error'), code: VoiceErrorCode }).strict(),
  /** The last event; the socket closes after it. */
  z.object({ type: z.literal('closed') }).strict(),
]);
export type VoiceServerEvent = z.infer<typeof VoiceServerEvent>;

/**
 * What a reviewer sees of one voice session (APP-104 §6): its words as heard, in order, how long
 * it ran and where it was transcribed. Saved and interrupted sessions only; never audio.
 */
export const SpokenSession = z
  .object({
    status: z.enum(['saved', 'interrupted']),
    provider: z.enum(['azure_speech', 'stand_in']),
    startedAt: z.string(),
    audioMs: z.number().int().nonnegative(),
    phrases: z.array(VoicePhrase),
  })
  .strict();
export type SpokenSession = z.infer<typeof SpokenSession>;
